<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DToggleField,
  DLoading,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  ParametersEmployeeBankAccounts,
  ReportEmployeeBankAccounts,
  generateEmployeeBankAccounts,
  getPayroll,
  getBanks,
  getEmployeesStandardEmployees,
  getReportRequest
} from '@/app/core/services/reports'
import { getAccountTypesByCountry } from '@/app/core/services/reports/banks'
usePayrollChange(() => back())

/* store, router, notifications */
const store = useUserStore()
const router = useRouter()
const storePayroll = usePayrollStore()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const itemsBanks = ref<any[]>([])
const itemsAccountTypes = ref<any[]>([])
const searchOrderBy = ref('')
const itemsOrderBy = ref<any[]>([
  { id: 'employeeCode', name: t('reports.columns.employeeCode') },
  { id: 'fullname', name: t('reports.columns.nameEmployee') },
  { id: 'fiscalId', name: t('reports.columns.fiscalId') }
])
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  banks: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  accountTypes: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') }))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<ParametersEmployeeBankAccounts>({
  initialValues: {
    orderBy: 'employeeCode',
    excludeDismissed: false,
    banks: [],
    accountTypes: []
  },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: orderBy } = useField<string>(ref('orderBy'))
const { value: excludeDismissed } = useField<boolean>(ref('excludeDismissed'))
const { value: banks } = useField<string[]>(ref('banks'))
const { value: accountTypes } = useField<string[]>(ref('accountTypes'))
const { value: employees } = useField<any[]>(ref('employees'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  const path = route.params.code as string
  const decodeCode = atob(path)
  /* Si estan todos seleccionados se envia vacio para no filtrar */
  const parameters = {
    ...values,
    banks: itemsBanks.value.length === values.banks.length ? [] : values.banks,
    accountTypes:
      itemsAccountTypes.value.length === values.accountTypes.length
        ? []
        : values.accountTypes
  }
  const createReport: ReportEmployeeBankAccounts = {
    code: decodeCode,
    parameters
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateEmployeeBankAccounts(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar empleados */
const listEmployees = async () => {
  employees.value = []
  itemsEmployees.value = []
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (payrolls.value?.length > 0) {
      const strPayrolls =
        payrolls.value.length === 0
          ? ''
          : "'" + payrolls.value.join("','") + "'"
      const employeesResult = await getEmployeesStandardEmployees(
        strPayrolls,
        excludeDismissed.value
      )
      itemsEmployees.value = employeesResult
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    }
    loading.value = false
    if (itemsEmployees.value.length === 0) {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [promisePayrolls, promiseBanks, promiseAccountTypes] =
      await Promise.all([
        getPayroll(store.user?.id as string),
        getBanks(),
        getAccountTypesByCountry(storePayroll.payroll?.country?.id)
      ])
    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsBanks.value = promiseBanks
    banks.value = itemsBanks.value.map((_: any) => _.id)
    itemsAccountTypes.value = promiseAccountTypes
    accountTypes.value = itemsAccountTypes.value.map((_: any) => _.id)
    await listEmployees()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.employeeBankAccounts')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="listEmployees" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.orderBy')"
                    v-model="orderBy"
                    :options="filterOptions(itemsOrderBy, searchOrderBy)"
                    @filter="searchOrderBy = $event" />
                </div>
                <div
                  class="col-span-6 sm:col-span-3 lg:col-span-2 flex justify-start items-center">
                  <DToggleField
                    :label="t('reports.fields.excludeTerminatedEmployees')"
                    @change="listEmployees"
                    v-model="excludeDismissed" />
                </div>
                <hr class="col-span-6" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.banks')"
                    v-model="banks"
                    :headerTitle="t('reports.fields.banks')"
                    :required="true"
                    :options="itemsBanks"
                    :error="errors.banks" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.accountTypes')"
                    v-model="accountTypes"
                    :headerTitle="t('reports.fields.accountTypes')"
                    :required="true"
                    :options="itemsAccountTypes"
                    :error="errors.accountTypes" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.excel')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
